import React, { useContext } from "react";

import InfoIcon from "@mui/icons-material/Info";
import {
  Box,
  Grid,
  InputAdornment,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import { makeStyles, withStyles } from "tss-react/mui";

import { colors } from "@/theme/overrides/colors";
import { handleChange } from "@/utils/hookForm";
import MeatIcon from "@assets/icons/beef.png";
import FruitIcon from "@assets/icons/fruits.png";
import DairyIcon from "@assets/icons/milk.png";
import StarchIcon from "@assets/icons/starch.png";
import SugarIcon from "@assets/icons/sugar-cube.png";
import FatIcon from "@assets/icons/trans-fats-free.png";
import VegetableIcon from "@assets/icons/vegetable.png";

import CalculationTable from "./CalculationTable";
import MealPlanFormStore from "../store/MealPlanFormStore";

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: theme.spacing(4),
    backgroundColor: "#fafafa",
  },
  icon: {
    marginRight: theme.spacing(3),
    height: 24,
    width: 24,
  },
  bold: {
    display: "inline",
    fontWeight: "bold",
  },
  autosuggest: {
    color: colors.grey,
    fontSize: 14,
    fontWeight: "bold",
  },
}));

const CustomTooltip = withStyles(Tooltip, (theme: Theme) => ({
  tooltip: {
    backgroundColor: colors.dark,
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    fontSize: 14,
  },
}));

const FoodExchangeCalculator: React.FC = observer(() => {
  const { classes } = useStyles();

  const store = useContext(MealPlanFormStore.Context);
  const fel = store.foodExchange;

  const _suggestionInfo = (
    suggestedValue: number,
    currentValue: number | string | undefined
  ) => {
    const title =
      currentValue != null || currentValue == "0"
        ? `Suggested value: ${suggestedValue}`
        : `Suggested remaining value: ${suggestedValue}`;
    return (
      <CustomTooltip title={title}>
        <InfoIcon fontSize="small" />
      </CustomTooltip>
    );
  };

  return (
    <Grid className={classes.root} container>
      <Grid item container xs={7}>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={VegetableIcon} alt="" />
              <Typography className={classes.bold}>Vegetable</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.vegetable}
              onChange={handleChange((value) => fel.set("vegetable", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={FruitIcon} alt="" />
              <Typography className={classes.bold}>Fruit</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.fruit}
              onChange={handleChange((value) => fel.set("fruit", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={SugarIcon} alt="" />
              <Typography className={classes.bold}>Sugar</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.sugar}
              onChange={handleChange((value) => fel.set("sugar", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={DairyIcon} alt="" />
              <Typography className={classes.bold}>Milk</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.milkNonFatSkim}
              onChange={handleChange(
                (value) => fel.set("milkNonFatSkim", value),
                { maxDecimalPlace: 2 }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Non fat skim"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.milkLowFat}
              onChange={handleChange((value) => fel.set("milkLowFat", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="Low fat"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.milkWhole}
              onChange={handleChange((value) => fel.set("milkWhole", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="Whole"
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={StarchIcon} alt="" />
              <Typography className={classes.bold}>Rice</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.riceLowProtein}
              onChange={handleChange(
                (value) => fel.set("riceLowProtein", value),
                { maxDecimalPlace: 2 }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Low protein"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.riceLowProtein,
                      fel.values.riceLowProtein
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.riceMediumProtein}
              onChange={handleChange(
                (value) => fel.set("riceMediumProtein", value),
                { maxDecimalPlace: 2 }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Medium protein"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.riceMediumProtein,
                      fel.values.riceMediumProtein
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.riceHighProtein}
              onChange={handleChange(
                (value) => fel.set("riceHighProtein", value),
                { maxDecimalPlace: 2 }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="High protein"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.riceHighProtein,
                      fel.values.riceHighProtein
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={MeatIcon} alt="" />
              <Typography className={classes.bold}>Meat</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.meatLowFat}
              onChange={handleChange((value) => fel.set("meatLowFat", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="Low fat"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.meatLowFat,
                      fel.values.meatLowFat
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.meatModerateFat}
              onChange={handleChange(
                (value) => fel.set("meatModerateFat", value),
                { maxDecimalPlace: 2 }
              )}
              type="number"
              size="small"
              margin="dense"
              helperText="Moderate fat"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.meatModerateFat,
                      fel.values.meatModerateFat
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.meatHighFat}
              onChange={handleChange((value) => fel.set("meatHighFat", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              helperText="High fat"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(
                      fel.autosuggest.meatHighFat,
                      fel.values.meatHighFat
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={3}>
            <Box display="flex" marginTop={3}>
              <img className={classes.icon} src={FatIcon} alt="" />
              <Typography className={classes.bold}>Fat</Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <TextField
              value={fel.values.fat}
              onChange={handleChange((value) => fel.set("fat", value), {
                maxDecimalPlace: 2,
              })}
              type="number"
              size="small"
              margin="dense"
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    className={classes.autosuggest}
                    position="end"
                  >
                    {_suggestionInfo(fel.autosuggest.fat, fel.values.fat)}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        <CalculationTable />
      </Grid>
    </Grid>
  );
});

export default FoodExchangeCalculator;
